import { WebSocketStatus } from "@hocuspocus/provider";
import { useEffect, useState } from "react";

const READ_ONLY_MESSAGE =
  "Topicflow is in read-only mode. Your notes are being saved locally, and will be synchronized the next time you visit.";
const NEW_VERSION_MESSAGE =
  "A new version of Topicflow is available. This field has been disabled until you reload the page.";
const CONNECTING_MESSAGE =
  "Trying to connect. Your notes are being saved locally, and will be synchronized once a connection is re-established.";

const computeWebsocketStatusMessage = ({
  isRealtimeEditorSchemaUpToDate,
  isFocused,
  status,
}: {
  isRealtimeEditorSchemaUpToDate: boolean;
  isFocused: boolean;
  status?: WebSocketStatus | null;
}): string | null => {
  // readOnlyMode is defined in `vite.html`
  if (window.readOnlyMode) return READ_ONLY_MESSAGE;
  if (isFocused && !isRealtimeEditorSchemaUpToDate) return NEW_VERSION_MESSAGE;
  if (isFocused && status === "connecting") return CONNECTING_MESSAGE;
  return null;
};

const WebsocketStatus = ({
  isRealtimeEditorSchemaUpToDate,
  isFocused,
  status,
  hideBanner = false,
  onMessageChange,
}: {
  isRealtimeEditorSchemaUpToDate: boolean;
  isFocused: boolean;
  status?: WebSocketStatus;
  hideBanner?: boolean;
  onMessageChange?: (message: string | null) => void;
}) => {
  const [debouncedStatus, setDebouncedStatus] =
    useState<WebSocketStatus | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedStatus(status || null), 5000);
    return () => {
      clearTimeout(timer);
    };
  }, [status]);

  const message = computeWebsocketStatusMessage({
    isRealtimeEditorSchemaUpToDate,
    isFocused,
    status: debouncedStatus,
  });

  useEffect(() => {
    onMessageChange?.(message);
  }, [message, onMessageChange]);

  // Reset the upstream message when this editor unmounts so a stale message
  // from a torn-down editor doesn't keep an indicator visible elsewhere.
  useEffect(() => {
    return () => onMessageChange?.(null);
  }, [onMessageChange]);

  if (hideBanner || !message) return null;

  return (
    <div className="bg-amber-100 text-xs text-amber-600 rounded-md my-1 py-1 px-2">
      {message}
    </div>
  );
};

export default WebsocketStatus;
